'use client';

import { useEffect, useMemo } from 'react';
import { useParams } from 'next/navigation';
import { getLocalizationInfo } from 'project.config';
import { productsIndex_AU, productsIndex_NZ } from 'helpers/constants/algolia';
import { facetsByAttribute, parseFacetConfigs, pricesByAttribute } from 'helpers/algolia/listing-facets';
import ProductsLoadError from 'components/commercetools-ui/atoms/products-load-error';
import ProductListSSR from 'components/commercetools-ui/organisms/product/product-list-ssr';
import SsrProductListProvider from 'components/commercetools-ui/organisms/product/product-list-ssr/context';
import type { TileLoadingStyle } from 'components/commercetools-ui/organisms/product/product-list-ssr/hooks/useTileLoading';
import { LocalDatasetProvider } from 'components/commercetools-ui/organisms/product/product-list-ssr/local-store';
import { SectionWithProductListingContent } from './types';

/** CMS `product_tiles_loading_style` → the grid's reveal mode. Blank or unknown → infinite scroll. */
const toLoadingStyle = (raw?: string): TileLoadingStyle => {
  const value = String(raw ?? '').trim().toLowerCase();
  if (value === 'pagination') return 'pagination';
  if (value === 'load more button') return 'loadMore';
  return 'infinite';
};

/**
 * V2 `section_with_product_listing` — the product listing page grid (SERVER PATH).
 *
 * Everything comes from the Contentstack block: the Algolia filter, the facet panel, the title,
 * category vs collection chrome and the tile loading style. Nothing is derived from the URL, the
 * Frontastic page configuration or the category tree. The products were preloaded server-side by
 * helpers/algolia/preload-cms-product-blocks (backend action → Redis) and arrive as
 * `content.plpDataset`; only the first tile page is in the SSR HTML, the rest is topped up after
 * hydration. Filtering, sorting and facet counts then run in memory
 * (product-list-ssr/local-store) — this section never queries Algolia from the browser, and imports
 * nothing from the /search listing.
 */
export default function SectionWithProductListing({ content }: { content: SectionWithProductListingContent }) {
  const { locale, slug } = useParams();
  const dataset = content?.plpDataset;

  // A missing dataset means the CMS `algolia_filters_query` is blank (the preload skips it), so no query
  // ran and the usual "[Algolia PLP] query" log below cannot either. Report it in the BROWSER console.
  const listingQuery = content?.algolia_filters_query;
  const listingTitle = content?.listing_title;
  const listingUid = content?._metadata?.uid;
  useEffect(() => {
    if (dataset) return;
    const reason = listingQuery?.trim()
      ? 'a query IS configured, but the server-side preload attached no dataset'
      : 'the CMS algolia_filters_query for this section is BLANK';
    console.error(
      '%c[Algolia PLP] listing NOT loaded — no Algolia query was run',
      'color:#dc2626;font-weight:bold',
      [
        '',
        `  reason:   ${reason}`,
        `  section:  ${listingTitle || '(untitled)'}`,
        `  cms uid:  ${listingUid || '(none)'}`,
        `  filters:  ${JSON.stringify(listingQuery ?? null)}`,
      ].join('\n'),
    );
  }, [dataset, listingQuery, listingTitle, listingUid]);

  // Log the Algolia query + product count on every listing — on success AND on failure/0-products, so it
  // is visible on any environment while the error message is shown. Pretty JSON, ready to paste into a
  // curl or the Algolia dashboard.
  const debug = dataset?.debug;
  useEffect(() => {
    if (!debug) return;
    const requestJson = debug.request ? `\n  request:  ${JSON.stringify(debug.request, null, 2)}` : '';
    const algoliaJson = debug.algoliaRequests
      ? `\n  sent to Algolia client.search (cache misses only): ${JSON.stringify(debug.algoliaRequests, null, 2)}`
      : '';
    if (debug.error) {
      console.error(
        `%c[Algolia PLP] load FAILED`,
        'color:#dc2626;font-weight:bold',
        `\n  products: ${debug.nbProducts}\n  error:    ${debug.error}${requestJson}${algoliaJson}`,
      );
    } else {
      console.log(
        `%c[Algolia PLP] query`,
        'color:#2563eb;font-weight:bold',
        `\n  products: ${debug.nbProducts}${requestJson}${algoliaJson}`,
      );
    }
  }, [debug]);

  const { currency } = getLocalizationInfo(locale as string);
  const facetConfigs = useMemo(() => parseFacetConfigs(content?.facets_configuration), [content?.facets_configuration]);
  const facetsConfiguration = useMemo(
    () => facetsByAttribute(facetConfigs, { currency, canonical: true }),
    [facetConfigs, currency],
  );
  const pricesConfiguration = useMemo(
    () => pricesByAttribute(facetConfigs, { currency, canonical: true }),
    [facetConfigs, currency],
  );

  if (!dataset) {
    return (
      <div id="plp-container" className="bg-[#EDEDED] px-24 pt-48 md:px-28 2xl:pt-80">
        {/* The heading stays so the visitor can see WHICH listing failed. */}
        {listingTitle && (
          <h1 className="w-full text-center text-38 font-medium leading-[3rem] text-[#090909] 2xl:text-48">
            {listingTitle}
          </h1>
        )}
        <ProductsLoadError className="min-h-[30vh] px-24 py-48" />
      </div>
    );
  }

  // The preload ran and failed after the backend's retries: show a retry message — never a browser
  // Algolia fallback.
  if (dataset.loadError) {
    return (
      <div id="plp-container">
        <ProductsLoadError />
      </div>
    );
  }

  // The last URL segment keys the persisted filters (localStorage) only — it no longer decides what
  // the listing fetches or what its heading says.
  const listingSlug = Array.isArray(slug) ? slug.at(-1) : (slug as string | undefined)?.split('/').at(-1);

  return (
    <LocalDatasetProvider dataset={dataset} baseIndex={locale === 'nz' ? productsIndex_NZ : productsIndex_AU}>
      <SsrProductListProvider>
        <ProductListSSR
          slug={listingSlug}
          title={listingTitle?.trim() ?? ''}
          isCollection={content.listing_type?.toLowerCase() === 'collection'}
          facetsConfiguration={facetsConfiguration}
          pricesConfiguration={pricesConfiguration}
          loadingStyle={toLoadingStyle(content.product_tiles_loading_style)}
        />
      </SsrProductListProvider>
    </LocalDatasetProvider>
  );
}
